import { UIPlugin } from '@uppy/core';
import { Provider } from '@uppy/companion-client';
import { ProviderViews } from '@uppy/provider-views';
import { h } from 'preact';
import httpRequest from 'utils/httpRequest';
import { prepareAncestryAuthUrl } from 'ecommerce/components/signInWithAncestry';

import ancestryApi from '../api/ancestry';
import { PHOTO_GRID_PAGE_SIZE } from '../polotno/photos/photoGrid';

const MIME_TYPE_BY_FEXT = {
  apng: 'image/apng',
  avif: 'image/avif',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  pjpeg: 'image/jpeg',
  pjp: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

export default class AncestryProvider extends UIPlugin {
  constructor(uppy, opts) {
    super(uppy, opts);
    this.id = 'ancestry';
    this.title = 'Ancestry';

    Provider.initPlugin(this, opts);

    this.icon = () =>
      h(
        'svg',
        {
          'aria-hidden': 'true',
          focusable: 'false',
          width: '32',
          height: '32',
          viewBox: '-40 -30 320 230',
        },
        h(
          'g',
          {
            fill: 'none',
            fillRule: 'evenodd',
          },
          h('path', {
            d:
              'M150.82 33.292c-17.654-19.168-35.307-16.14-38.334-15.637 1.513.504 3.027 33.796 21.69 51.45 12.61 11.6 38.335 7.565 38.335 7.565s-4.54-24.715-21.69-43.378m21.187 65.07c-5.55 0-28.248 2.018-43.38 20.68-15.637 19.673-15.133 35.31-18.664 38.84 3.027 1.01 34.805 4.035 48.424-16.645 13.114-19.17 13.62-39.85 13.62-42.876M33.797 84.74c3.53.506 15.637 16.143 37.327 18.16 23.707 2.524 31.777-9.582 32.283-9.582 0-1.514-19.168-22.194-40.353-22.194-14.63.503-29.257 13.617-29.257 13.617m86.254 10.594c-.504.504-20.175 26.734-66.582 19.672C16.142 108.954 2.523 88.272 0 87.768c3.027-4.036 31.275-26.734 64.06-26.23 32.284 1.01 55.99 19.168 55.99 19.168h20.68c-16.644-5.044-36.82-14.123-45.9-34.3-8.07-18.662-6.557-29.76-10.088-45.396 0 0 37.326-3.027 66.078 15.636 26.23 17.15 40.857 62.547 41.362 64.06h16.14L204.793 0l20.176.504c-3.53 21.186 1.01 172.51 0 172.51-2.017.503-19.168 1.51-20.68-2.524 0 0 4.035-74.148 4.54-75.662H193.19c0 1.01-1.513 42.875-33.29 65.07-31.78 22.194-77.68 15.636-77.68 15.636 5.55-8.575 0-17.654 16.14-42.37 18.664-28.247 43.38-38.336 42.877-38.336-1.513.506-20.68.506-21.186.506',
            fill: '#9CBE30',
          }),
        ),
      );

    const fetchAncestryImage = async (url, callback) => {
      try {
        const response = await httpRequest(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        return callback(true, blobUrl);
      } catch (e) {
        return callback(false, null);
      }
    };

    this.provider = new Provider(uppy, {
      companionUrl: this.opts.companionUrl,
      provider: 'ancestry',
      pluginId: this.id,
    });

    this.provider.authUrl = () => {
      return prepareAncestryAuthUrl('referer');
    };

    this.provider.list = async directory => {
      if (this.opts.ancestryAuth == null) {
        this.setPluginState({ authenticated: false, error: true });
        return {
          items: [],
        };
      }

      const api = ancestryApi(this.opts.paraboAccessToken, this.opts.ancestryAuth);

      if (directory != null) {
        const [treeId, pageId] = directory.split(':::');
        const { data, error } = await api.getTreeMedia(treeId, pageId, {
          pageSize: PHOTO_GRID_PAGE_SIZE,
        });
        if (error) {
          if (error.code === 401) {
            this.setPluginState({ authenticated: false, error });
          }
          return {
            items: [],
          };
        }

        const items = await Promise.all(
          data.map(medium => {
            return new Promise(res => {
              const mimeType = MIME_TYPE_BY_FEXT[medium.Fext];
              if (uppy.opts.restrictions.allowedFileTypes.indexOf(mimeType) < 0) {
                return res({ valid: false });
              }

              return fetchAncestryImage(medium.ThumbnailUrl, (success, blobUrl) => {
                if (success) {
                  res({
                    isFolder: false,
                    icon: blobUrl,
                    name: [medium.MediumId, medium.Fext].join('.'),
                    mimeType,
                    id: medium.MediumId,
                    size: null,
                    thumbnail: medium.ThumbnailUrl,
                    requestPath: [medium.MediumId, medium.Url].join(':::'),
                  });
                } else {
                  res({
                    valid: false,
                  });
                }
              });
            });
          }),
        );

        return {
          items: items.filter(item => item.valid !== false),
          nextPagePath: [treeId, parseInt(pageId, 10) + 1].join(':::'),
        };
      }

      const { data, error } = await api.getTrees();
      if (error) {
        if (error.code === 401) {
          this.setPluginState({ authenticated: false, error });
        }
        return {
          items: [],
        };
      }

      return {
        items: data.map(tree => ({
          isFolder: true,
          icon: 'folder',
          name: tree.name,
          id: tree.id,
          requestPath: [tree.id, 1].join(':::'),
          isCheckboxDisabled: true,
        })),
      };
    };

    this.provider.fileUrl = requestPath => {
      const [id, url] = requestPath.split(':::');
      return `${this.opts.companionUrl}/${this.provider.provider
        }/get/${id}?url=${encodeURIComponent(url)}`;
    };

    this.onFirstRender = this.onFirstRender.bind(this);
    this.render = this.render.bind(this);
  }

  install() {
    this.view = new ProviderViews(this, {
      provider: this.provider,
    });
    const { target } = this.opts;

    if (target) {
      this.mount(target, this);
    }
  }

  onMount() {
    super.onMount();
  }

  uninstall() {
    this.view.tearDown();
    this.unmount();
  }

  onFirstRender() {
    return Promise.all([
      new Promise(res => {
        httpRequest(`${this.opts.companionUrl}/${this.id}/token`)
          .then(response => {
            return response.json();
          })
          .then(json => {
            this.provider.setAuthToken(json.authToken);
            this.setPluginState({
              authenticated: this.getPluginState().error == null,
            });
            return res();
          })
          .catch(e => {
            this.setPluginState({ error: true });
          });
      }),
      this.view.getFolder(),
    ]);
  }

  render(state) {
    const viewOptions = {};

    if (this.getPluginState().files.length && !this.getPluginState().folders.length) {
      viewOptions.viewType = 'grid';
    }
    viewOptions.showFilter = false;

    return this.view.render(state, viewOptions);
  }
}
AncestryProvider.VERSION = '1.0.0';
